/**
 * Whether this build has a backend to show, and where to send someone who
 * wants to see it.
 *
 * Both skins ask these two questions and both must get the same answer, which
 * is why they are here rather than decided twice in markup. A skin that worked
 * out for itself when to offer the link would eventually disagree with the
 * other one about a half-finished build.
 *
 * The browser is deliberately not told how to reach Xano. It knows only that a
 * backend exists and that Hatch has a door; the address behind that door is
 * minted per click, server-side, against a credential no page may hold. See
 * api/src/xano/impersonate.ts for why it cannot be a link rendered up front.
 */

import { useEffect, useState } from "react";

/**
 * The narrowest viewport the handoff is offered on.
 *
 * Tailwind's `md`, which is a tablet held in portrait and up. Below it the
 * offer is withheld entirely rather than made smaller, because what is behind
 * the door is Xano's own workspace UI and that UI is not built for a phone —
 * a visitor who takes the offer on the device the QR code sent them to gets a
 * worse impression of the product than the one who never saw the button.
 *
 * A width rather than a touch or pointer test: an iPad is touch-only and is
 * exactly the case this is meant to keep.
 */
const HANDOFF_MIN_WIDTH_PX = 768;

/** The subset of the stream this decision reads. */
export interface BackendOffer {
  outcome: string | null;
  /** The ephemeral tenant name, or null when the deploy named none. */
  xanoTenant: string | null;
}

/**
 * Only a finished, successful build with a known tenant gets the offer.
 *
 * The outcome check is not redundant with the tenant check. A tenant name
 * survives on the session row, so a viewer who reloads onto a session that
 * later failed would otherwise be handed a door into a backend that was never
 * published — the route refuses it, but a button that always errors is worse
 * than no button.
 */
export function offersBackend(offer: BackendOffer): boolean {
  return offer.outcome === "succeeded" && offer.xanoTenant !== null;
}

/**
 * Whether this viewport is wide enough to be sent into Xano at all.
 *
 * Read live rather than once at mount: a phone rotated into landscape is still
 * a phone by this measure, but a tablet rotated into portrait crosses the line
 * in the other direction, and a done screen that decided at mount would be
 * wrong for whichever half of that pair it did not start in.
 *
 * `innerWidth` rather than `matchMedia` because this has to agree with the CSS
 * breakpoints under a test runner as well as in a browser, and jsdom answers
 * every media query `false` while reporting an honest width.
 */
export function useWideEnoughForHandoff(): boolean {
  const [wide, setWide] = useState(
    () => typeof window === "undefined" || window.innerWidth >= HANDOFF_MIN_WIDTH_PX,
  );

  useEffect(() => {
    const read = () => setWide(window.innerWidth >= HANDOFF_MIN_WIDTH_PX);
    read();
    window.addEventListener("resize", read);
    // Some mobile browsers report the pre-rotation width to a `resize` fired on
    // orientation change; both are listened for so the later of the two wins.
    window.addEventListener("orientationchange", read);
    return () => {
      window.removeEventListener("resize", read);
      window.removeEventListener("orientationchange", read);
    };
  }, []);

  return wide;
}

/**
 * `offersBackend`, narrowed by the viewport — what a skin should actually ask.
 *
 * Both halves are here rather than one in lib and one in markup so that a skin
 * cannot end up offering the door on a phone by forgetting the second half.
 */
export function useOffersBackend(offer: BackendOffer): boolean {
  return useWideEnoughForHandoff() && offersBackend(offer);
}

/**
 * The Hatch route that opens this session's backend.
 *
 * Same-origin and cookie-authorised, like every other session route. The id is
 * encoded because it lands in a path segment; ids are generated by Hatch and
 * contain nothing that needs it today, which is exactly the assumption worth
 * not building on.
 */
export function backendHandoffPath(sessionId: string): string {
  return `/api/sessions/${encodeURIComponent(sessionId)}/xano`;
}

/**
 * The three claims both skins make, shared because they are claims rather than
 * decoration.
 *
 * Layout, weight, colour, and whether there is an emoji beside it stay with
 * each skin — those are voice. The sentences do not, because each one asserts
 * something about the world that has to be true in both places at once. The
 * read-only line is the sharp case: it describes what
 * `api/src/xano/impersonate.ts` actually mints, and a skin that drifted into
 * promising more would be lying about a security property rather than choosing
 * a tone.
 *
 * A skin that genuinely needs its own wording should stop importing these
 * rather than edit them — that is a deliberate divergence, and it should read
 * as one.
 */
export const HANDOFF_COPY = {
  /** Beside the address: the difference between a preview and a site. */
  hosted: "Really hosted, really online — send it to anyone.",
  /** The offer as one line, for a skin with room for one. */
  cta: "Your backend is real too — open it in Xano",
  /**
   * The same offer as a heading and a button, for a skin that gives it a card.
   *
   * Split rather than replaced: the claim and the action are one sentence when
   * they share a line and two things when they do not, and a card whose button
   * reads "Your backend is real too — open it in Xano" is a card with a
   * sentence for a button.
   */
  heading: "Your backend is real too",
  action: "Open it in Xano",
  /** What they will find, and what they will not be able to do to it. */
  scope: "Browse the tables, APIs, and functions that were just built. Read-only.",
} as const;
